/* Hallmark · pre-emit critique: P5 H5 E5 S5 R5 V4 */
/* Hallmark · component: reply-template-manager · genre: modern-minimal · theme: Lantern Outreach
 * states: default · hover · focus · active · disabled · loading · error · success
 * contrast: pass (40–41) · mobile: pass (320 · 375 · 414 · 768)
 */

'use client';

import { useEffect, useId, useMemo, useRef, useState, type CSSProperties } from 'react';
import { AlertCircle, Check, Loader2, Pencil, Plus, Trash2, X } from 'lucide-react';
import { Modal } from '@/components/ui-v2';
import { cn } from '@/lib/utils';

export type ReplyTemplate = {
    id: string;
    label: string;
    body: string;
};

export type ReplyTemplateDraft = {
    id?: string;
    label: string;
    body: string;
};

export type ReplyTemplatesModalState =
    | 'default'
    | 'hover'
    | 'focus'
    | 'active'
    | 'disabled'
    | 'loading'
    | 'error'
    | 'success';

type EditorMode = 'list' | 'editor';

const forcedActionState: Record<ReplyTemplatesModalState, string> = {
    default: '',
    hover: 'bg-[var(--surface-hover)]',
    focus: 'outline-2 outline-offset-2 outline-[var(--accent-emphasis)]',
    active: 'translate-y-px bg-[var(--surface-active)]',
    disabled: 'cursor-not-allowed opacity-55',
    loading: 'cursor-wait',
    error: '',
    success: '',
};

const baseButtonClass =
    'inline-flex min-h-11 shrink-0 items-center justify-center gap-2 rounded-[var(--radius-md)] px-3 text-[13px] font-medium leading-none whitespace-nowrap transition-colors duration-[var(--duration-fast)] focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[var(--accent-emphasis)] active:translate-y-px disabled:cursor-not-allowed disabled:opacity-55 motion-reduce:transform-none motion-reduce:transition-none';

const secondaryButtonClass = cn(
    baseButtonClass,
    'border border-[var(--border-default)] bg-[var(--surface-primary)] text-[var(--text-primary)] hover:bg-[var(--surface-hover)]'
);

const primaryButtonClass = cn(
    baseButtonClass,
    'border-0'
);

const primaryButtonStyle = {
    background: 'var(--accent)',
    color: 'white',
} satisfies CSSProperties;

const iconButtonClass =
    'inline-flex min-h-11 min-w-11 shrink-0 items-center justify-center rounded-[var(--radius-md)] text-[var(--text-tertiary)] transition-colors duration-[var(--duration-fast)] hover:text-[var(--text-primary)] focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[var(--accent-emphasis)] active:translate-y-px disabled:cursor-not-allowed disabled:opacity-55 motion-reduce:transform-none motion-reduce:transition-none';

const fieldClass =
    'min-h-11 w-full rounded-[var(--radius-md)] border border-[var(--border-default)] bg-[var(--surface-primary)] px-3 text-[13px] leading-5 text-[var(--text-primary)] outline-none transition-none placeholder:text-[var(--text-tertiary)] disabled:cursor-not-allowed disabled:opacity-55';

function readableTemplatePreview(body: string): string {
    return body
        .replace(/\{\{\s*(?:candidate\.)?firstName\s*\}\}/gi, '[First name]')
        .replace(/\{\{\s*(?:calendar\.link|bookingLink)\s*\}\}/gi, '[Booking link]')
        .replace(/\s+/g, ' ')
        .trim();
}

function templateCountLabel(count: number): string {
    return `${count} ${count === 1 ? 'template' : 'templates'}`;
}

export function ReplyTemplatesPanel({
    templates,
    onSave,
    onDelete,
    onClose,
    state = 'default',
    initialMode = 'list',
}: {
    templates: ReplyTemplate[];
    onSave: (template: ReplyTemplateDraft) => void;
    onDelete: (templateId: string) => void;
    onClose: () => void;
    state?: ReplyTemplatesModalState;
    initialMode?: EditorMode;
}) {
    const [mode, setMode] = useState<EditorMode>(initialMode);
    const [editingTemplate, setEditingTemplate] = useState<ReplyTemplate | null>(null);
    const [templateName, setTemplateName] = useState('');
    const [templateBody, setTemplateBody] = useState('');
    const [nameTouched, setNameTouched] = useState(false);
    const [bodyTouched, setBodyTouched] = useState(false);
    const [templateToDelete, setTemplateToDelete] = useState<ReplyTemplate | null>(null);
    const firstActionRef = useRef<HTMLButtonElement>(null);
    const nameInputRef = useRef<HTMLInputElement>(null);
    const cancelDeleteRef = useRef<HTMLButtonElement>(null);
    const fieldId = useId();
    const nameInputId = `${fieldId}-name`;
    const nameErrorId = `${fieldId}-name-error`;
    const bodyInputId = `${fieldId}-body`;
    const bodyHelpId = `${fieldId}-body-help`;
    const deleteDialogTitleId = `${fieldId}-delete-title`;
    const deleteDialogBodyId = `${fieldId}-delete-body`;

    const pending = state === 'loading';
    const unavailable = state === 'disabled' || pending;
    const nameInvalid = (nameTouched || state === 'error') && !templateName.trim();
    const bodyInvalid = (bodyTouched || state === 'error') && !templateBody.trim();
    const editorTitle = editingTemplate ? 'Edit reply template' : 'New reply template';

    useEffect(() => {
        const focusTarget = mode === 'editor' ? nameInputRef.current : firstActionRef.current;
        const frame = window.requestAnimationFrame(() => focusTarget?.focus());
        return () => window.cancelAnimationFrame(frame);
    }, [mode]);

    useEffect(() => {
        if (!templateToDelete) return;
        const frame = window.requestAnimationFrame(() => cancelDeleteRef.current?.focus());
        return () => window.cancelAnimationFrame(frame);
    }, [templateToDelete]);

    const status = useMemo(() => {
        if (state === 'success') {
            return {
                icon: Check,
                copy: 'Template saved.',
                color: 'var(--fg-success)',
                role: 'status' as const,
            };
        }
        if (state === 'error') {
            return {
                icon: AlertCircle,
                copy: 'Template could not be saved. Check the highlighted fields.',
                color: 'var(--fg-danger)',
                role: 'alert' as const,
            };
        }
        return null;
    }, [state]);

    const openNewTemplate = () => {
        setEditingTemplate(null);
        setTemplateName('');
        setTemplateBody('');
        setNameTouched(false);
        setBodyTouched(false);
        setMode('editor');
    };

    const openTemplate = (template: ReplyTemplate) => {
        setEditingTemplate(template);
        setTemplateName(template.label);
        setTemplateBody(template.body);
        setNameTouched(false);
        setBodyTouched(false);
        setMode('editor');
    };

    const returnToList = () => {
        setMode('list');
        setEditingTemplate(null);
        setNameTouched(false);
        setBodyTouched(false);
    };

    const saveTemplate = () => {
        setNameTouched(true);
        setBodyTouched(true);
        const label = templateName.trim();
        const body = templateBody.trim();
        if (!label || !body || unavailable) return;
        onSave({ id: editingTemplate?.id, label, body });
        returnToList();
    };

    const cancelDelete = () => {
        setTemplateToDelete(null);
        window.requestAnimationFrame(() => firstActionRef.current?.focus());
    };

    const confirmDelete = () => {
        if (!templateToDelete || unavailable) return;
        onDelete(templateToDelete.id);
        setTemplateToDelete(null);
        window.requestAnimationFrame(() => firstActionRef.current?.focus());
    };

    const StatusIcon = status?.icon;

    if (templateToDelete) {
        return (
            <div
                role="alertdialog"
                aria-modal="true"
                aria-labelledby={deleteDialogTitleId}
                aria-describedby={deleteDialogBodyId}
                className="flex min-h-0 flex-col bg-[var(--surface-primary)] text-[var(--text-primary)]"
                onKeyDown={(event) => {
                    if (event.key !== 'Escape') return;
                    event.preventDefault();
                    event.stopPropagation();
                    cancelDelete();
                }}
            >
                <header className="flex min-h-14 shrink-0 items-center justify-between gap-3 border-b border-[var(--border-muted)] px-4 sm:px-5">
                    <h2 id={deleteDialogTitleId} className="truncate text-[15px] font-semibold leading-5 tracking-[-0.01em]">
                        Delete template
                    </h2>
                    <button
                        type="button"
                        onClick={cancelDelete}
                        aria-label="Cancel template deletion"
                        className={iconButtonClass}
                    >
                        <X aria-hidden="true" size={16} strokeWidth={1.75} />
                    </button>
                </header>

                <div className="px-4 py-6 sm:px-5">
                    <div className="flex h-9 w-9 items-center justify-center rounded-full bg-[color-mix(in_srgb,var(--fg-danger)_10%,transparent)] text-[var(--fg-danger)]">
                        <Trash2 aria-hidden="true" size={16} strokeWidth={1.9} />
                    </div>
                    <p className="mt-4 text-[15px] font-semibold leading-5">
                        Delete “{templateToDelete.label}”?
                    </p>
                    <p
                        id={deleteDialogBodyId}
                        className="mt-2 max-w-md text-[13px] leading-5 text-[var(--text-secondary)]"
                    >
                        This reply template will be permanently removed from this browser.
                    </p>
                </div>

                <footer className="flex min-h-16 shrink-0 items-center justify-end gap-2 border-t border-[var(--border-muted)] px-4 sm:px-5">
                    <button
                        ref={cancelDeleteRef}
                        type="button"
                        onClick={cancelDelete}
                        className={secondaryButtonClass}
                    >
                        Cancel
                    </button>
                    <button
                        type="button"
                        onClick={confirmDelete}
                        className={cn(
                            baseButtonClass,
                            'border-0 bg-[var(--fg-danger)] text-white hover:brightness-95'
                        )}
                    >
                        <Trash2 aria-hidden="true" size={14} strokeWidth={1.9} />
                        Delete
                    </button>
                </footer>
            </div>
        );
    }

    return (
        <div className="flex max-h-[min(76dvh,40rem)] min-h-0 flex-col bg-[var(--surface-primary)] text-[var(--text-primary)]">
            <header className="flex min-h-14 shrink-0 items-center justify-between gap-3 border-b border-[var(--border-muted)] px-4 sm:px-5">
                <div className="min-w-0">
                    <h2 className="truncate text-[15px] font-semibold leading-5 tracking-[-0.01em]">
                        {mode === 'editor' ? editorTitle : 'Reply templates'}
                    </h2>
                    {mode === 'editor' && (
                        <p className="mt-1 text-[12px] leading-4 text-[var(--text-secondary)]">
                            Inserted into the composer for review.
                        </p>
                    )}
                </div>
                <button
                    type="button"
                    onClick={onClose}
                    disabled={pending}
                    aria-label="Close reply templates"
                    className={iconButtonClass}
                >
                    <X aria-hidden="true" size={16} strokeWidth={1.75} />
                </button>
            </header>

            <div className="min-h-0 overflow-y-auto px-4 py-4 sm:px-5">
                {mode === 'list' ? (
                    <div className="flex flex-col gap-3">
                        {templates.length > 0 && (
                            <div className="flex min-w-0 items-center justify-between gap-3">
                                <p className="min-w-0 text-[12px] leading-5 text-[var(--text-secondary)]">
                                    <span className="font-medium tabular-nums text-[var(--text-primary)]">
                                        {templateCountLabel(templates.length)}
                                    </span>
                                    <span aria-hidden="true"> · </span>
                                    Saved to this browser
                                </p>
                                <button
                                    ref={firstActionRef}
                                    type="button"
                                    onClick={() => {
                                        if (!unavailable) openNewTemplate();
                                    }}
                                    disabled={state === 'disabled'}
                                    aria-disabled={unavailable || undefined}
                                    aria-busy={pending || undefined}
                                    className={cn(secondaryButtonClass, forcedActionState[state])}
                                >
                                    {pending ? (
                                        <Loader2
                                            aria-hidden="true"
                                            size={14}
                                            strokeWidth={2}
                                            className="animate-spin motion-reduce:animate-none"
                                        />
                                    ) : (
                                        <Plus aria-hidden="true" size={14} strokeWidth={2} />
                                    )}
                                    {pending ? 'Loading…' : 'New template'}
                                </button>
                            </div>
                        )}

                        {status && StatusIcon && (
                            <p
                                role={status.role}
                                className="flex items-center gap-2 text-[12px] font-medium"
                                style={{ color: status.color }}
                            >
                                <StatusIcon aria-hidden="true" size={14} strokeWidth={2} />
                                {status.copy}
                            </p>
                        )}

                        {templates.length === 0 ? (
                            <div className="flex flex-col items-start gap-5 py-2 sm:flex-row sm:items-center sm:justify-between">
                                <div className="min-w-0">
                                    <p className="text-[13px] font-medium">No reply templates</p>
                                    <p className="mt-1 text-[12px] leading-5 text-[var(--text-secondary)]">
                                        Save replies you use often.
                                    </p>
                                </div>
                                <button
                                    ref={firstActionRef}
                                    type="button"
                                    onClick={() => {
                                        if (!unavailable) openNewTemplate();
                                    }}
                                    disabled={state === 'disabled'}
                                    aria-disabled={unavailable || undefined}
                                    className={cn(secondaryButtonClass, forcedActionState[state])}
                                >
                                    <Plus aria-hidden="true" size={14} strokeWidth={2} />
                                    New template
                                </button>
                            </div>
                        ) : (
                            <ul
                                aria-label="Saved reply templates"
                                className="divide-y divide-[var(--border-muted)] border-y border-[var(--border-muted)]"
                            >
                                {templates.map((template) => (
                                    <li
                                        key={template.id}
                                        className={cn(
                                            'flex min-w-0 items-center gap-1 rounded-[var(--radius-sm)] transition-colors duration-[var(--duration-fast)]',
                                            unavailable
                                                ? 'cursor-not-allowed'
                                                : 'hover:bg-[var(--surface-hover)] focus-within:bg-[var(--surface-hover)]'
                                        )}
                                    >
                                        <button
                                            type="button"
                                            onClick={() => openTemplate(template)}
                                            disabled={unavailable}
                                            className="group flex min-h-14 min-w-0 flex-1 items-center gap-3 rounded-[var(--radius-sm)] px-1 text-left focus-visible:outline-2 focus-visible:outline-offset-[-2px] focus-visible:outline-[var(--accent-emphasis)] active:opacity-80 disabled:cursor-not-allowed disabled:opacity-55"
                                        >
                                            <span className="min-w-0 flex-1">
                                                <span className="block truncate text-[13px] font-medium leading-5">
                                                    {template.label}
                                                </span>
                                                <span className="mt-1 block truncate text-[12px] leading-4 text-[var(--text-secondary)]">
                                                    {readableTemplatePreview(template.body)}
                                                </span>
                                            </span>
                                            <Pencil
                                                aria-hidden="true"
                                                size={14}
                                                strokeWidth={1.75}
                                                className="shrink-0 text-[var(--text-tertiary)] group-hover:text-[var(--text-secondary)]"
                                            />
                                        </button>
                                        <button
                                            type="button"
                                            onClick={() => setTemplateToDelete(template)}
                                            disabled={unavailable}
                                            aria-label={`Delete ${template.label}`}
                                            className={cn(
                                                iconButtonClass,
                                                'hover:text-[var(--fg-danger)] focus-visible:outline-[var(--fg-danger)]'
                                            )}
                                        >
                                            <Trash2 aria-hidden="true" size={14} strokeWidth={1.75} />
                                        </button>
                                    </li>
                                ))}
                            </ul>
                        )}
                    </div>
                ) : (
                    <div className="flex flex-col gap-4">
                        {status && StatusIcon && (
                            <p
                                role={status.role}
                                className="flex items-start gap-2 text-[12px] font-medium leading-5"
                                style={{ color: status.color }}
                            >
                                <StatusIcon aria-hidden="true" size={14} strokeWidth={2} className="mt-1 shrink-0" />
                                {status.copy}
                            </p>
                        )}

                        <div>
                            <label
                                htmlFor={nameInputId}
                                className="mb-2 block text-[13px] font-medium leading-5"
                            >
                                Template name
                            </label>
                            <input
                                ref={nameInputRef}
                                id={nameInputId}
                                value={templateName}
                                maxLength={80}
                                placeholder="Availability follow-up"
                                disabled={unavailable}
                                aria-required="true"
                                aria-invalid={nameInvalid || undefined}
                                aria-describedby={nameInvalid ? nameErrorId : undefined}
                                onBlur={() => setNameTouched(true)}
                                onChange={(event) => setTemplateName(event.target.value)}
                                className={cn(
                                    fieldClass,
                                    nameInvalid
                                        ? 'border-[var(--fg-danger)] hover:border-[var(--fg-danger)] focus-visible:border-[var(--fg-danger)]'
                                        : 'hover:border-[var(--text-primary)] focus-visible:border-[var(--text-primary)]'
                                )}
                            />
                            <p
                                id={nameErrorId}
                                className="mt-1 min-h-5 text-[12px] leading-5 text-[var(--fg-danger)]"
                            >
                                {nameInvalid ? 'Add a short name so you can find this template.' : ''}
                            </p>
                        </div>

                        <div>
                            <label
                                htmlFor={bodyInputId}
                                className="mb-2 block text-[13px] font-medium leading-5"
                            >
                                Message
                            </label>
                            <textarea
                                id={bodyInputId}
                                rows={6}
                                value={templateBody}
                                maxLength={5000}
                                placeholder="Write the reply you want to reuse."
                                disabled={unavailable}
                                aria-required="true"
                                aria-invalid={bodyInvalid || undefined}
                                aria-describedby={bodyHelpId}
                                onBlur={() => setBodyTouched(true)}
                                onChange={(event) => setTemplateBody(event.target.value)}
                                className={cn(
                                    fieldClass,
                                    'min-h-36 resize-y py-3',
                                    bodyInvalid
                                        ? 'border-[var(--fg-danger)] hover:border-[var(--fg-danger)] focus-visible:border-[var(--fg-danger)]'
                                        : 'hover:border-[var(--text-primary)] focus-visible:border-[var(--text-primary)]'
                                )}
                            />
                            <div
                                id={bodyHelpId}
                                className="mt-1 flex min-h-5 items-start justify-between gap-3 text-[12px] leading-5"
                            >
                                <span className={bodyInvalid ? 'text-[var(--fg-danger)]' : 'text-[var(--text-secondary)]'}>
                                    {bodyInvalid
                                        ? 'Write the message this template should insert.'
                                        : 'Variables: {{firstName}} · {{bookingLink}}'}
                                </span>
                                <span className="shrink-0 tabular-nums text-[var(--text-secondary)]">
                                    {templateBody.length} / 5000
                                </span>
                            </div>
                        </div>
                    </div>
                )}
            </div>

            {mode === 'editor' && (
                <footer className="flex min-h-16 shrink-0 items-center justify-end gap-2 border-t border-[var(--border-muted)] px-4 sm:px-5">
                    <button
                        type="button"
                        onClick={returnToList}
                        disabled={pending}
                        className={secondaryButtonClass}
                    >
                        Cancel
                    </button>
                    <button
                        type="button"
                        onClick={saveTemplate}
                        disabled={state === 'disabled'}
                        aria-disabled={unavailable || undefined}
                        aria-busy={pending || undefined}
                        className={cn(primaryButtonClass, forcedActionState[state])}
                        style={primaryButtonStyle}
                    >
                        {pending && (
                            <Loader2
                                aria-hidden="true"
                                size={14}
                                strokeWidth={2}
                                className="animate-spin motion-reduce:animate-none"
                            />
                        )}
                        {pending ? 'Saving…' : editingTemplate ? 'Save changes' : 'Add template'}
                    </button>
                </footer>
            )}
        </div>
    );
}

export function ReplyTemplatesModal({
    open,
    templates,
    onSave,
    onDelete,
    onClose,
}: {
    open: boolean;
    templates: ReplyTemplate[];
    onSave: (template: ReplyTemplateDraft) => void;
    onDelete: (templateId: string) => void;
    onClose: () => void;
}) {
    return (
        <Modal open={open} onClose={onClose} width={540} className="outreach-skin">
            {open && (
                <ReplyTemplatesPanel
                    templates={templates}
                    onSave={onSave}
                    onDelete={onDelete}
                    onClose={onClose}
                />
            )}
        </Modal>
    );
}
